import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ErrorBanner } from "../components/ui";

export default function LoginPage() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [signupAllowed, setSignupAllowed] = useState(true);
  const [hasUsers, setHasUsers] = useState(true);
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ signupAllowed: boolean; hasUsers: boolean }>("/auth/status")
      .then((r) => {
        setSignupAllowed(r.signupAllowed);
        setHasUsers(r.hasUsers);
        // A fresh deployment has no account yet — start on the sign-up form.
        if (!r.hasUsers) setMode("signup");
      })
      .catch(() => undefined);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "signin") await signIn(login, password);
      else await signUp(login, password, name);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <span className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-xl bg-accent-600 text-lg font-bold text-white">
            ₹
          </span>
          <h1 className="text-xl font-semibold text-ink-950">Card Points Tracker</h1>
          <p className="mt-1 text-sm text-ink-500">
            {mode === "signin" ? "Sign in to your tracker" : hasUsers ? "Create an account" : "Set up your account"}
          </p>
        </div>

        <div className="card-surface p-6">
          {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <label className="label" htmlFor="name">Name</label>
                <input
                  id="name"
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                />
              </div>
            )}
            <div>
              <label className="label" htmlFor="login">Email or mobile number</label>
              {/* Plain text rather than type="email", which would refuse a
                  number before it reached the server. */}
              <input
                id="login"
                className="input"
                type="text"
                required
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="you@example.com or 98765 43210"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <div>
              <label className="label" htmlFor="password">Password</label>
              <input
                id="password"
                className="input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
              />
            </div>
            <button className="btn-primary w-full" disabled={busy}>
              {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
            </button>
          </form>

          {signupAllowed && (
            <p className="mt-4 text-center text-sm text-ink-500">
              {mode === "signin" ? "No account yet? " : "Already have an account? "}
              <button
                className="font-medium text-accent-700 underline"
                onClick={() => {
                  setMode(mode === "signin" ? "signup" : "signin");
                  setError(null);
                }}
              >
                {mode === "signin" ? "Create one" : "Sign in"}
              </button>
            </p>
          )}
          {!signupAllowed && mode === "signin" && (
            <p className="mt-4 text-center text-xs text-ink-400">Sign-ups are disabled on this deployment.</p>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-ink-400">
          Your data stays in your own database. Nothing is shared with anyone.
        </p>
      </div>
    </div>
  );
}
